import { LogOut, Shield, Menu, X } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { useSessionTimeout } from '@/features/auth/hooks/useSessionTimeout';
import { platformRoleLabel } from '@/lib/permissions';
import { endSession, getSessionEmail } from '@/lib/session';

import styles from './AdminLayout.module.scss';
import { NAV_ITEMS } from './navigation';

export function AdminLayout(): JSX.Element {
  const location = useLocation();
  const email = getSessionEmail();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // OS-1717 — el menú muestra solo las secciones del rol (el API es la fuente de verdad).
  const { can, session } = usePlatformPermissions();
  const navItems = NAV_ITEMS.filter((item) => can(item.permission));

  // Cierra el drawer de navegación al cambiar de ruta (mobile).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // OS-1710 — cierre por inactividad (30 min) y al expirar el token.
  useSessionTimeout();

  function handleLogout(): void {
    // Limpia todas las claves de sesión, incluido el token MFA parcial.
    endSession('logout');
  }

  return (
    <div className={styles.shell}>
      <aside className={[styles.sidebar, mobileNavOpen ? styles.sidebarOpen : ''].join(' ')}>
        <div className={styles.logo}>
          <div className={styles.logoMark}>
            <Shield size={14} />
          </div>
          <div className={styles.logoText}>
            OpsSynk
            <span className={styles.logoSub}>Admin Console</span>
          </div>
        </div>

        <nav className={styles.nav}>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [styles.navItem, isActive ? styles.active : ''].join(' ')
              }
            >
              <Icon className={styles.navIcon} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <span className={styles.adminEmail}>
            {email}
            {session ? <span className={styles.adminRole}>{platformRoleLabel(session.role)}</span> : null}
          </span>
          <button className={styles.logoutBtn} onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {mobileNavOpen ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Cerrar menú"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <div className={styles.main}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.menuBtn}
            aria-label={mobileNavOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((value) => !value)}
          >
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <span className={styles.topbarTitle}>
            {NAV_ITEMS.find((n) => location.pathname.startsWith(n.to))?.label ?? 'Admin'}
          </span>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
